import { useState } from 'react';
import Drawer from '~/components/Drawer';
import { inputStyles, labelStyles, requiredStyles } from './styles';
import {
    updateApplicationInsuranceInfo,
    updateApplicationInsuranceInfoViaApplication,
} from '~/services/campaign';

interface Props {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    application?: unknown;
    campaignRequestId?: number;
    applicationId?: number;
    onUpdate?: (response: any) => void;
}

const InsuranceInfoDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    application,
    campaignRequestId,
    applicationId,
    onUpdate,
}) => {
    const applicationData = application as {
        ins_has_none?: boolean;
        ins_has_medicare?: boolean;
        ins_has_medicaid?: boolean;
        ins_has_private?: boolean;
        ins_has_cobra?: boolean;
        ins_unknown?: boolean;
        ins_comments?: string;
        ins_company?: string;
        ins_premium?: number;
    };

    // Determine whether insurance has been answered yet. A blank application
    // has no "none" flag, no yes-type flags, and no supporting fields — in that
    // case leave the selection unset (null) so neither radio is pre-checked.
    const hasYesData =
        applicationData?.ins_has_medicare ||
        applicationData?.ins_has_medicaid ||
        applicationData?.ins_has_private ||
        applicationData?.ins_has_cobra ||
        applicationData?.ins_unknown ||
        !!applicationData?.ins_company ||
        !!applicationData?.ins_comments ||
        !!applicationData?.ins_premium;
    const initialHasInsurance: boolean | null =
        applicationData?.ins_has_none === true
            ? false
            : hasYesData
              ? true
              : null;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        hasInsurance: initialHasInsurance as boolean | null,
        hasMedicare: applicationData?.ins_has_medicare || false,
        hasMedicaid: applicationData?.ins_has_medicaid || false,
        hasPrivate: applicationData?.ins_has_private || false,
        hasCobra: applicationData?.ins_has_cobra || false,
        unknown: applicationData?.ins_unknown || false,
        comments: applicationData?.ins_comments || '',
        company: applicationData?.ins_company || '',
        premium: applicationData?.ins_premium || '',
    });

    const handleChange = (field: string, value: string | number | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleCheckboxChange = (field: string, checked: boolean) => {
        setFormData((prev) => ({ ...prev, [field]: checked }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // "Do you have insurance?" is required — don't let an unanswered
        // selection be saved as "No" via the !hasInsurance fallback below.
        if (formData.hasInsurance === null) {
            console.error('Please select whether the patient has insurance');
            return;
        }

        // Validate that we have either campaignRequestId or applicationId
        if (!campaignRequestId && !applicationId) {
            console.error(
                'Either Campaign Request ID or Application ID is required to save insurance data'
            );
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                ins_has_medicare: formData.hasMedicare,
                ins_has_medicaid: formData.hasMedicaid,
                ins_has_private: formData.hasPrivate,
                ins_has_cobra: formData.hasCobra,
                ins_has_none: !formData.hasInsurance,
                ins_unknown: formData.unknown,
                ins_comments: formData.comments,
                ins_company: formData.company,
                ins_premium: formData.premium
                    ? parseFloat(formData.premium.toString())
                    : null,
            };

            console.log('Saving insurance data:', payload);

            let response;

            // Use the appropriate endpoint based on which ID is provided
            if (applicationId) {
                response = await updateApplicationInsuranceInfoViaApplication(
                    applicationId,
                    payload
                );
            } else if (campaignRequestId) {
                response = await updateApplicationInsuranceInfo(
                    campaignRequestId,
                    payload
                );
            }

            if (onUpdate && response) {
                onUpdate(response);
            }

            setIsOpen(false);
        } catch (error) {
            console.error('Error saving insurance data:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title='Insurance Info'>
            <form onSubmit={handleSubmit}>
                <label htmlFor='ins-has-insurance' className={labelStyles}>
                    Do you have Insurance?
                    <span className={requiredStyles}>*</span>
                </label>
                <div className='mt-2 flex gap-4'>
                    <label className='flex items-center'>
                        <input
                            type='radio'
                            name='hasInsurance'
                            value='yes'
                            checked={formData.hasInsurance === true}
                            onChange={() => handleChange('hasInsurance', true)}
                            className='mr-2'
                        />
                        Yes
                    </label>
                    <label className='flex items-center'>
                        <input
                            type='radio'
                            name='hasInsurance'
                            value='no'
                            checked={formData.hasInsurance === false}
                            onChange={() => handleChange('hasInsurance', false)}
                            className='mr-2'
                        />
                        No
                    </label>
                </div>

                {formData.hasInsurance === true && (
                    <>
                        <label className={labelStyles}>
                            Check all that apply:
                            <span className={requiredStyles}>*</span>
                        </label>
                        <div className='mt-2 space-y-2'>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasMedicare}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasMedicare',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2'
                                />
                                I have Medicare
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasMedicaid}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasMedicaid',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2'
                                />
                                I have Medicaid
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasPrivate}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasPrivate',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2'
                                />
                                I have Private insurance
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasCobra}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasCobra',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2'
                                />
                                I have Private insurance through COBRA
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.unknown}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'unknown',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2'
                                />
                                I don't know
                            </label>
                        </div>

                        <label htmlFor='ins-comments' className={labelStyles}>
                            Comments About Insurance
                        </label>
                        <textarea
                            id='ins-comments'
                            className={`${inputStyles} w-full`}
                            value={formData.comments}
                            onChange={(e) =>
                                handleChange('comments', e.target.value)
                            }
                            rows={4}
                        />

                        <label htmlFor='ins-company' className={labelStyles}>
                            Insurance Company
                            <span className={requiredStyles}>*</span>
                        </label>
                        <input
                            type='text'
                            id='ins-company'
                            className={`${inputStyles} w-full`}
                            value={formData.company}
                            onChange={(e) =>
                                handleChange('company', e.target.value)
                            }
                        />

                        <label htmlFor='ins-premium' className={labelStyles}>
                            Monthly Premium Amount (Copay)
                        </label>
                        <div className='flex items-center gap-2'>
                            <span className='text-[16px]/[20px]'>$</span>
                            <input
                                type='number'
                                id='ins-premium'
                                className={`${inputStyles} w-full`}
                                value={formData.premium}
                                onChange={(e) =>
                                    handleChange('premium', e.target.value)
                                }
                                placeholder='0.00'
                                step='0.01'
                                min='0'
                            />
                        </div>
                        <p className='mt-1 text-[12px]/[16px] text-[#949599]'>
                            Leave empty if you do not have a Premium or Copay
                        </p>
                    </>
                )}

                <div className='mb-[30px] flex gap-[16px]'>
                    <button
                        type='button'
                        className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        onClick={() => setIsOpen(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:cursor-not-allowed disabled:opacity-50'
                        disabled={isSubmitting || formData.hasInsurance === null}
                    >
                        {isSubmitting ? (
                            <span className='flex items-center justify-center'>
                                <svg
                                    className='mr-3 h-5 w-5 animate-spin'
                                    viewBox='0 0 24 24'
                                >
                                    <circle
                                        className='opacity-25'
                                        cx='12'
                                        cy='12'
                                        r='10'
                                        stroke='currentColor'
                                        strokeWidth='4'
                                    />
                                    <path
                                        className='opacity-75'
                                        fill='currentColor'
                                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                    />
                                </svg>
                                Saving...
                            </span>
                        ) : (
                            'Save'
                        )}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default InsuranceInfoDrawer;
